import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { CustomPaginationComponent } from '../../../../components/DataTable/CustomPaginationComponent'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'
import { CcqCards, ccqOffersDeepAnalysis, type CcqCard } from './ccqResultView'
import { CcqClientRow, CcqClientRowSkeleton } from './CcqClientRow'
import { CcqActionFilterControl } from './CcqActionFilter'
import {
  CCQ_ACTION_FILTER_WITH_EMPTY,
  CCQ_ACTION_FILTER_WITHOUT_EMPTY,
  CCQ_CARD_EMPTY_DONE,
  CCQ_LIST_LOADING,
  CCQ_SEARCH_EMPTY,
  CCQ_SEARCH_PLACEHOLDER,
  CCQ_SELECT_ALL,
} from './ccqRunCopy'
import { useCcqCollectCardResults } from './useCcqCardResults'
import { useCcqClientsReady } from './useCcqClientView'
import { useCcqRun } from './useCcqRun'
import { easeOutCubic } from './useWaveTween'
import { useCcqCursor } from './useCcqListCursor'
import { CcqActionFilters, CcqPanelOrigins, hasActionsFor, useCcqWorkspace } from './useCcqWorkspace'
import styles from './CcqGroupCard.module.scss'

const SCROLL_TO_TOP_MS = 200

interface CcqFullListProps {
  card: CcqCard
  isLeaving: boolean
}

export const CcqFullList: React.FC<CcqFullListProps> = ({ card, isLeaving }) => {
  const { isDone, counts } = useCcqRun()
  const { selection, toggleSelected, setSelection } = useCcqWorkspace()
  const cursor = useCcqCursor()
  const { search, appliedSearch, setSearch, filter: actionFilter, page, setPage, size: pageSize, setSize: setPageSize, read: fullList } = cursor.list
  const { selected } = cursor.details
  const panelStep = cursor.details.lastMove.direction
  const collectCardResults = useCcqCollectCardResults()
  const listRef = useRef<HTMLUListElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollFrame = useRef(0)
  const isOpeningRef = useRef(true)
  const selectedId = selected?.origin === CcqPanelOrigins.FULL_LIST ? selected.resultId : null

  const pageResults = fullList.results
  const arePageClientsReady = useCcqClientsReady(pageResults)
  // Rows show a whole page at a time: until its results and their clients are in, it's skeletons.
  const isPageLoading = pageResults.length === 0 ? fullList.isFetching : !arePageClientsReady
  const rowsOnPage = (fullList.hasLoaded ? fullList.totalElements : counts[card]) - (page - 1) * pageSize
  const skeletonRows = pageResults.length > 0 ? pageResults.length : Math.max(1, Math.min(pageSize, rowsOnPage))
  // Checkboxes only where a footer has bulk actions, which wait for the check to finish.
  const isSelectable = isDone && (card === CcqCards.MATCHED || ccqOffersDeepAnalysis(card))
  // Select all covers the whole card as searched and filtered, not just this page.
  const allSelected = fullList.totalElements > 0 && selection.length === fullList.totalElements
  const someSelected = selection.length > 0 && !allSelected

  const emptyMessage = (() => {
    if (appliedSearch.trim() !== '') {
      return CCQ_SEARCH_EMPTY
    }
    if (actionFilter === CcqActionFilters.WITH) {
      return CCQ_ACTION_FILTER_WITH_EMPTY
    }
    if (actionFilter === CcqActionFilters.WITHOUT) {
      return CCQ_ACTION_FILTER_WITHOUT_EMPTY
    }
    return CCQ_CARD_EMPTY_DONE
  })()

  // Collecting a large card takes a while; a result for a list that has since changed or closed is dropped.
  const listIdentity = `${card}|${appliedSearch}|${actionFilter}`
  const currentListRef = useRef<string | null>(listIdentity)
  useEffect(() => {
    currentListRef.current = listIdentity
    return () => {
      currentListRef.current = null
    }
  }, [listIdentity])

  // Centers the selected row on open and when the panel's arrows step; a clicked row is already in view.
  useEffect(() => {
    const isOpening = isOpeningRef.current
    isOpeningRef.current = false
    if (selectedId === null || (panelStep === 0 && !isOpening)) {
      return
    }
    const row = listRef.current?.querySelector<HTMLElement>('[aria-current="true"]')
    if (!row || typeof row.scrollIntoView !== 'function') {
      return
    }
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    row.scrollIntoView({ block: 'center', behavior: prefersReducedMotion ? 'auto' : 'smooth' })
  }, [selectedId, page, panelStep])

  // Opening from See all removes the link that had focus; stepping in from the panel keeps it on the arrows.
  useEffect(() => {
    if (document.activeElement === null || document.activeElement === document.body) {
      searchRef.current?.focus()
    }
  }, [])

  // Hand-rolled because the browser's smooth scroll is slower and can't be tuned.
  const scrollToTop = () => {
    const scroller = scrollRef.current
    if (!scroller) {
      return
    }
    cancelAnimationFrame(scrollFrame.current)
    const from = scroller.scrollTop
    if (from === 0 || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      scroller.scrollTop = 0
      return
    }
    const startedAt = performance.now()
    const step = (now: number) => {
      const t = Math.min((now - startedAt) / SCROLL_TO_TOP_MS, 1)
      scroller.scrollTop = from * (1 - easeOutCubic(t))
      if (t < 1) {
        scrollFrame.current = requestAnimationFrame(step)
      }
    }
    scrollFrame.current = requestAnimationFrame(step)
  }

  useEffect(
    () => () => {
      cancelAnimationFrame(scrollFrame.current)
    },
    [],
  )

  const handleChangePage = (nextPage: number) => {
    setPage(nextPage)
    scrollToTop()
  }

  const handleChangePageSize = (nextSize: number) => {
    setPageSize(nextSize)
    scrollToTop()
  }

  const handleView = (result: CrossClientQueryResult) => {
    cursor.open(result.id, card)
  }

  const handleSelectAll = async () => {
    if (allSelected) {
      setSelection([])
      return
    }
    const requestedList = listIdentity
    try {
      const results = await collectCardResults(card, appliedSearch, hasActionsFor(actionFilter))
      if (currentListRef.current !== requestedList) {
        return
      }
      setSelection(results.map((result) => result.id))
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className={classNames(isLeaving ? styles.listOut : styles.listIn, styles.fill, 'd-flex flex-column')}>
      {/* Padding lines up with CcqClientRow: select all over the row checkboxes, the filter with the names. */}
      <div className={classNames('d-flex align-items-center gap-3 pb-2', isSelectable ? 'ps-3 pe-2' : 'zocks-pl-2.75 pe-4')}>
        {isSelectable ? (
          <Form.Check
            type='checkbox'
            className={classNames(styles.checkbox, 'm-0 flex-shrink-0')}
            ref={(input: HTMLInputElement | null) => {
              if (input) {
                input.indeterminate = someSelected
              }
            }}
            checked={allSelected}
            onChange={() => void handleSelectAll()}
            aria-label={CCQ_SELECT_ALL}
          />
        ) : null}
        <CcqActionFilterControl card={card} onChange={scrollToTop} />
        <label
          className={classNames(
            styles.search,
            'd-flex align-items-center gap-2 flex-grow-1 mw-0 mb-0 px-2 border zocks-border-0.5 zocks-border-color-gray-300 zocks-bg-color-white',
          )}
        >
          <i className='bi bi-search zocks-text-gray-600' aria-hidden='true' />
          <input
            type='search'
            className={classNames(styles.searchInput, 'flex-grow-1 mw-0 p-0 border-0 bg-transparent paragraph-small zocks-text-gray-900')}
            placeholder={CCQ_SEARCH_PLACEHOLDER}
            aria-label={CCQ_SEARCH_PLACEHOLDER}
            ref={searchRef}
            value={search}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.currentTarget.value)}
          />
        </label>
      </div>

      <div ref={scrollRef} className={styles.fillScroll}>
        {isPageLoading ? (
          <>
            <span className='visually-hidden' role='status'>
              {CCQ_LIST_LOADING}
            </span>
            <ul className='list-unstyled d-flex flex-column gap-1 mb-0'>
              {Array.from({ length: skeletonRows }, (_, index) => (
                <CcqClientRowSkeleton key={index} showsEmail hasWideColumns hasCheckbox={isSelectable} />
              ))}
            </ul>
          </>
        ) : pageResults.length === 0 ? (
          <span className='d-block paragraph-small zocks-text-gray-700 zocks-pl-2.75 pe-3 py-3'>{emptyMessage}</span>
        ) : (
          <ul ref={listRef} className='list-unstyled d-flex flex-column gap-1 mb-0'>
            {pageResults.map((result) => (
              <CcqClientRow
                key={result.id}
                result={result}
                card={card}
                isSelected={cursor.isSelected(result.id, card)}
                showsEmail
                hasWideColumns
                checkbox={isSelectable ? { checked: selection.includes(result.id), onChange: () => toggleSelected(result.id) } : null}
                onView={handleView}
              />
            ))}
          </ul>
        )}

        {fullList.totalElements > 0 ? (
          <div className='px-3 pb-3'>
            <CustomPaginationComponent
              rowsPerPage={pageSize}
              rowCount={fullList.totalElements}
              currentPage={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangePageSize}
              scrollMenuIntoView
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
